"use client";

import { useMemo, useState } from "react";
import { HINT } from "@/components/tool-kit/styles";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import { PreviewTable } from "@/components/tools/stats-research/preview-table";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { NumberFormatter } from "@/lib/tool-kit/stats-descriptive/format";
import {
  columnName,
  type Dataset,
  parseDataset,
} from "@/lib/tool-kit/stats-regression/dataset";
import type { HeaderMode } from "@/lib/tool-kit/stats-research/matrix";

/*
 * The paste box every multi-variable regression tool opens with: a text area for a
 * spreadsheet paste, the header-row choice, a preview of what was read, and notes about
 * blanks. Every word arrives through `labels`, built from the calling widget's strings.
 */

export interface DatasetLabels {
  dataLabel: string;
  dataHint: string;
  placeholder: string;
  loadExample: string;
  clear: string;
  headerLabel: string;
  headerAuto: string;
  headerYes: string;
  headerNo: string;
  /** "Read {rows} × {columns}." */
  readSummary: string;
  rowCount: string;
  columnCount: string;
  moreRows: string;
  /** "one: {count} cell is blank or NA.|other: …" */
  missingCells: string;
  /** "Variable {number}" */
  variableName: string;
}

export function useDatasetInput() {
  const [raw, setRaw] = useState("");
  const [headerMode, setHeaderMode] = useState<HeaderMode>("auto");
  const dataset = useMemo(
    () => parseDataset(raw, headerMode),
    [raw, headerMode]
  );
  return { raw, setRaw, headerMode, setHeaderMode, dataset };
}

export type DatasetInputState = ReturnType<typeof useDatasetInput>;

/** The display name of every column, numbering the unnamed ones. */
export function columnNames(dataset: Dataset, template: string): string[] {
  return dataset.columns.map((column, index) =>
    columnName(column, index, (number) => t(template, { number }))
  );
}

export function DatasetInput({
  id,
  state,
  labels,
  example,
  locale,
  fmt,
  rowsShown = 6,
}: {
  id: string;
  state: DatasetInputState;
  labels: DatasetLabels;
  /** The text the "load example" button pastes. */
  example: string;
  locale: string;
  fmt: NumberFormatter;
  rowsShown?: number;
}) {
  const { dataset } = state;
  const names = columnNames(dataset, labels.variableName);
  const plural = (template: string, count: number) =>
    tPlural(template, locale, count, { count: fmt(count) }).trim();
  const missing = dataset.columns.reduce((sum, c) => sum + c.missing, 0);
  const hasData = dataset.rowCount > 0 && dataset.columns.length > 0;
  const body = (column: Dataset["columns"][number], row: number) =>
    column.raw[row] === "" ? "—" : column.raw[row];

  return (
    <div className="space-y-3">
      <PasteBox
        actions={[
          {
            label: labels.loadExample,
            onClick: () => {
              state.setHeaderMode("auto");
              state.setRaw(example);
            },
          },
          { label: labels.clear, onClick: () => state.setRaw("") },
        ]}
        header={{
          label: labels.headerLabel,
          value: state.headerMode,
          onChange: state.setHeaderMode,
          options: [
            { value: "auto", label: labels.headerAuto },
            { value: "yes", label: labels.headerYes },
            { value: "no", label: labels.headerNo },
          ],
        }}
        hint={labels.dataHint}
        id={id}
        label={labels.dataLabel}
        onChange={state.setRaw}
        placeholder={labels.placeholder}
        rows={9}
        value={state.raw}
      />
      {hasData ? (
        <div className="space-y-2">
          <p className={HINT}>
            {t(labels.readSummary, {
              rows: plural(labels.rowCount, dataset.rowCount),
              columns: plural(labels.columnCount, dataset.columns.length),
            })}{" "}
            {missing > 0 ? plural(labels.missingCells, missing) : null}
          </p>
          <PreviewTable
            head={names}
            limit={rowsShown}
            more={(hidden) => plural(labels.moreRows, hidden)}
            rows={Array.from({ length: dataset.rowCount }, (_, row) =>
              dataset.columns.map((column) => body(column, row))
            )}
          />
        </div>
      ) : null}
    </div>
  );
}
